import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import PostAddIcon from "@mui/icons-material/PostAdd";
import {
  Box,
  Chip,
  Select,
  Typography,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { addItemsModalStyles } from "./styles";
import { S3CredentialsContextInterface } from "../../contexts";
import { UserDetailsContext } from "../../contexts";
import { AuthContext, AuthContextInterface } from "../../contexts";
import { S3CredentialsContext } from "../../contexts";
import { FileState, ProjectFile, TrackProcessorInfo } from "../../proto/api_pb";
import { awaitableUploadToS3 } from "../../utils/S3Utils";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { postProjectFiles } from "../../services/projectService";
import { RpcError } from "grpc-web";
import { FileStateReverseMap } from "../SingleProject/types";
import { AddItemsModalProps } from "./types";
import { BORDER_COLOR } from "../../theme";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { postTrackBatch } from "../../services/trackService";
import CustomButton from "../CustomButton/CustomButton";
import FolderUpload from "./FolderUpload";
import Mixpanel from "../../utils/mixpanel";
const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const AddItemsModal: React.FC<AddItemsModalProps> = ({
  setUploadingFilesDetailsList,
  setIsUploading,
  setUploadingCardsStatusList,
  setIsUploadProcessing,
  setFolderUploadInfo,
  getAllFoldersForProject,
  setIsFilesUpdated,
  setIsCountUpdated,
}) => {
  const [open, setOpen] = React.useState(false);
  const [selectedFiles, setSelectedFiles] = React.useState<File[]>([]);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    React.useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const { userId } = React.useContext<AuthContextInterface>(AuthContext);
  const { enterprise, storageUsage, isEnterpriseAccount, userName } =
    React.useContext(UserDetailsContext);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { id } = useParams();
  const [uploadType, setUploadType] = React.useState<string>("files");
  const ACCEPTED_FILE_TYPES = "image/*,audio/*,video/*,.pdf,.zip,.mkv";
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const handleCloseBatchTrackAddModal = () => {
    setUploadType("files");
    handleClose();
  };

  const handleOpenMessageModal = () => {
    toast.success("Folder Uploaded Successfully");
    getAllFoldersForProject();
  };
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setSelectedFiles([]);
    setOpen(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const normalizedFiles = Array.from(files).map((file) => {
        const normalizedFileName = file.name.replace(/[^\w\s.-]/g, "_");
        return new File([file], normalizedFileName, { type: file.type });
      });
      setSelectedFiles((prevFiles) => [...prevFiles, ...normalizedFiles]);
    }
  };
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      const normalizedFiles = Array.from(event.dataTransfer.files).map(
        (file) => {
          const normalizedFileName = file.name.replace(/[^\w\s.-]/g, "_");
          return new File([file], normalizedFileName, { type: file.type });
        }
      );
      setSelectedFiles((prevFiles) => [...prevFiles, ...normalizedFiles]);
    }
  };

  const handleProgress = (pNum: number, idx: number, filesList: any[]) => {
    filesList[idx].uploadProgress = Math.floor(pNum);
    const modifiedList = [...filesList];
    setUploadingFilesDetailsList(modifiedList);
  };

  // Process files in batches
  const processFilesInBatches = async (
    files: File[],
    filesList: any[],
    userId: string,
    awsAccessKeyId: string,
    awsSecretAccessKey: string,
    awsSessionToken: string
  ) => {
    const allKeys: string[] = [];

    // Calculate how many batches we need
    const totalBatches = Math.ceil(files.length / 10);

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * 10;
      const end = Math.min(start + 10, files.length);
      const batchFiles = files.slice(start, end);
      const batchPromises: Promise<string>[] = [];

      // Create promises for this batch
      for (let i = 0; i < batchFiles.length; i++) {
        const file = batchFiles[i];
        const globalIndex = start + i;

        const uploadPromise = awaitableUploadToS3(
          userId,
          file,
          `project-file`,
          file.type,
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          (progress) => handleProgress(progress, globalIndex, filesList)
        );

        batchPromises.push(uploadPromise);
      }

      // Wait for this batch to complete before moving to next batch
      const batchKeys = await Promise.all(batchPromises);
      allKeys.push(...batchKeys);
    }

    return allKeys;
  };

  const onSubmit = async () => {
    const audioExtensions = ["mp3", "wav", "ogg", "flac"];
    const filesList = selectedFiles.map((file) => ({
      file,
      name: file.name,
      uploadProgress: 0,
      isCardOpen: true,
    }));
    setUploadingFilesDetailsList(filesList);
    setUploadingCardsStatusList(
      selectedFiles.map((file) => ({
        name: file.name,
        isCardClosed: false,
      }))
    );
    try {
      setIsUploading(true);
      handleClose();
      const audioFilesProcessorInfoList: ProjectFile[] = [];
      const otherFilesProcessorInfoList: ProjectFile[] = [];
      if (!awsAccessKeyId || !awsSecretAccessKey || !awsSessionToken) {
        toast.error("AWS credentials are missing. Please try again.");
        return;
      }

      const keys = await processFilesInBatches(
        selectedFiles,
        filesList,
        userId,
        awsAccessKeyId,
        awsSecretAccessKey,
        awsSessionToken
      );
      for (let i = 0; i < keys.length; i++) {
        const file = selectedFiles[i];
        const fileUrl = `${ASSETS_URL}/${keys[i]}`;
        const newFile = new ProjectFile();
        newFile.setFileName(file.name);
        newFile.setFileUrl(fileUrl);
        newFile.setFileState(FileStateReverseMap[FileState.FILE_STATE_ACTIVE]);
        newFile.setFileSize(file.size);
        newFile.setOwnerId(userId);
        newFile.setProjectId(id ?? "");
        newFile.setEnterpriseId(enterprise?.id as string);
        newFile.setType(file.type);
        const extension = file.name.split(".").pop()?.toLowerCase();
        if (extension && audioExtensions.includes(extension)) {
          audioFilesProcessorInfoList.push(newFile);
        } else {
          otherFilesProcessorInfoList.push(newFile);
          Mixpanel.track("File Uploaded", {
            userName: userName,
            fileTitle: file.name,
          });
        }
      }
      const token = await getValidAccessToken();
      if (audioFilesProcessorInfoList.length > 0) {
        const totalAudioFiles: TrackProcessorInfo[] = [];
        audioFilesProcessorInfoList.forEach((a) => {
          const t = new TrackProcessorInfo();
          t.setFilename(a.getFileName());
          t.setTrackUrl(a.getFileUrl());
          totalAudioFiles.push(t);
          Mixpanel.track("Track Uploaded", {
            userName: userName,
            songTitle: a.getFileName(),
          });
        });
        const postBatchTrackResponse = await postTrackBatch(
          totalAudioFiles,
          "",
          "",
          [],
          [],
          userId,
          "",
          "",
          "",
          token,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
        if (postBatchTrackResponse instanceof RpcError) {
          throw postBatchTrackResponse;
        }
        console.log("Audio Files Processed:", audioFilesProcessorInfoList);
      }
      if (otherFilesProcessorInfoList.length > 0) {
        const res = await postProjectFiles(token, otherFilesProcessorInfoList);
        if (res instanceof RpcError) {
          throw res;
        }
        console.log("Other Files Processed:", otherFilesProcessorInfoList);
      }
      setIsFilesUpdated(true);
      setIsCountUpdated(true);
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddFiles = () => {
    if (
      enterprise &&
      enterprise.storageLimit &&
      Math.min((storageUsage / enterprise?.storageLimit) * 100, 100) >= 100
    ) {
      toast.warn("Please upgrade your enterprise storage to add more files");
    } else {
      handleClickOpen();
    }
  };

  const handleSubmit = () => {
    if (selectedFiles.length === 0) {
      toast.warn("Please select a file to upload");
    } else {
      onSubmit();
    }
  };

  const handleUploadTypeChange = () => {
    if (uploadType === "files") {
      setUploadType("folder");
    } else {
      setUploadType("files");
    }
  };
  return (
    <>
      <CustomButton
        text="Add Files"
        icon={PostAddIcon}
        onClick={handleAddFiles}
      />
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle sx={{ textAlign: "center", fontWeight: "bold" }}>
          {"Add Files"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              width: isSmallScreen ? "100%" : "400px",
            }}
          >
            <label htmlFor="audio" className="uploadLabel">
              Select upload type
              <span className="asterisk">*</span>
            </label>
            <Select
              value={uploadType}
              onChange={handleUploadTypeChange}
              fullWidth
              sx={{ marginBottom: "10px" }}
              MenuProps={{
                PaperProps: { sx: { padding: 0 } },
                MenuListProps: { sx: { padding: 0 } },
              }}
            >
              <MenuItem value={"files"}>Files</MenuItem>
              <MenuItem value={"folder"}>Folder</MenuItem>
            </Select>

            {uploadType === "folder" && (
              <FolderUpload
                setIsUploading={setIsUploading}
                handleCloseBatchTrackAddModal={handleCloseBatchTrackAddModal}
                setIsUploadProcessing={setIsUploadProcessing}
                setFolderUploadInfo={setFolderUploadInfo}
                handleOpenMessageModal={handleOpenMessageModal}
              />
            )}
            {uploadType === "files" && (
              <Box
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  ...addItemsModalStyles.uploadingBox,
                  ...{
                    height: selectedFiles.length === 0 ? "200px" : "auto",
                    border: `2px dashed ${BORDER_COLOR}`,
                  },
                }}
              >
                <>
                  <CloudUploadIcon sx={addItemsModalStyles.cloudUploadIcon} />
                  <Typography
                    variant="body1"
                    sx={addItemsModalStyles.typography}
                  >
                    Drag & Drop files here or click the button below to upload.
                  </Typography>
                </>
                <>
                  <label htmlFor="file-upload">
                    <input
                      id="file-upload"
                      type="file"
                      multiple
                      accept={ACCEPTED_FILE_TYPES}
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      sx={addItemsModalStyles.chooseMoreFilesButton}
                    >
                      Choose More Files
                    </Button>
                  </label>
                </>
                {selectedFiles.length > 0 && (
                  <Box sx={{ marginTop: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      Selected Files:
                    </Typography>
                    <Box sx={addItemsModalStyles.chipContainer}>
                      {selectedFiles.map((file, index) => (
                        <Chip
                          key={index}
                          label={file.name}
                          icon={<InsertDriveFileIcon />}
                          color="primary"
                          variant="outlined"
                          sx={addItemsModalStyles.chip}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        {uploadType === "files" && (
          <DialogActions sx={addItemsModalStyles.buttonsContainer}>
            <CustomButton
              text="Add"
              onClick={handleSubmit}
              customStyles={{ width: "150px" }}
            />
            <CustomButton
              text="Cancel"
              onClick={handleClose}
              isSkipButton={true}
              customStyles={{ width: "150px" }}
            />
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};
